import React, { useState, useEffect, useRef } from 'react';

const normalizeId = (id) => String(id || '').trim();

const CompanyMultiSelect = ({
  label,
  value = [],
  onChange,
  companies = [],
  loading = false,
  placeholder = 'Click to select companies',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedIds = value.map(normalizeId).filter(Boolean);
  const searchTerm = search.trim().toLowerCase();

  const getCompanyLabel = (id) => {
    const company = companies.find((item) => normalizeId(item.id) === normalizeId(id));
    if (!company) return id;
    return company.company_registration_number ? `${company.name} (${company.company_registration_number})` : company.name;
  };

  const filteredCompanies = companies.filter((company) => {
    const labelText = getCompanyLabel(company.id).toLowerCase();
    return labelText.includes(searchTerm);
  });

  const toggleCompany = (id) => {
    const normalized = normalizeId(id);
    if (selectedIds.includes(normalized)) {
      onChange(selectedIds.filter((item) => item !== normalized));
    } else {
      onChange([...selectedIds, normalized]);
    }
  };

  const removeCompany = (id, e) => {
    e.stopPropagation();
    const normalized = normalizeId(id);
    onChange(selectedIds.filter((item) => item !== normalized));
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full min-h-[42px] px-3 py-2 border border-gray-200 rounded-xl text-sm text-left bg-white hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
      >
        {selectedIds.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg text-xs font-medium"
              >
                {getCompanyLabel(id)}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => removeCompany(id, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      removeCompany(id, e);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 leading-none"
                >
                  ×
                </span>
              </span>
            ))}
          </div>
        )}
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <p className="px-3 py-4 text-sm text-gray-400 text-center">Loading companies...</p>
            ) : filteredCompanies.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-400 text-center">No companies found</p>
            ) : (
              filteredCompanies.map((company) => {
                const id = normalizeId(company.id);
                const isSelected = selectedIds.includes(id);

                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleCompany(id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors ${
                      isSelected ? 'bg-primary text-white' : 'text-ink-table hover:bg-primary-soft'
                    }`}
                  >
                    <span>{getCompanyLabel(id)}</span>
                    {isSelected && <span className="text-xs font-medium">Selected</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyMultiSelect;
